import _, { chain } from 'underscore';
import $ from 'jquery';
import URI from 'urijs';
import React from 'react';
import ReactDOM from 'react-dom';
import { mergeStyleSets, Callout, DirectionalHint } from '@fluentui/react';

import { DeterministicWrapperView } from '@bingads-webui/deterministic-wrapper-view';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { getFilteredCampaignTypeFromRawFilter } from '@bingads-webui-campaign/campaign-ui-common';
import { showLabelsPopup, filterOnLabelClick, navigateToManagePage } from '@bingads-webui-campaign/labels';
import { DeliveryStatusPopupFactory, isDeliveryStatusDisapproved, registerCountryListEvent } from '@bingads-webui-campaign/delivery-status-popup-factory';
import { updateGridQuery, appendScopeParameter, setUpErrorHandling } from '@bingads-webui-campaign/summary-grid-helper';
import { StyledMMABulkConversionView } from '@bingads-webui-campaign/responsive-ad-editor';
import { pgridFactory } from '@bingads-webui-campaign/pgrid-factory';
import { PageReady } from '@bingads-webui/page-ready';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';
import { keyVault } from '@bingads-webui-react/popup-manager';
import { HelpLink } from '@bingads-webui-react/help-link';

import { renderSuggestionPopup } from './ad-strength/suggestion-popup';
import { instrumentationSchema } from './instrumentation-schema';
import { getReasonTemplates } from './editorial-reason/show-rejection-reasons';

const appConfig = globalContext.require('component/config/index');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ads-tab');
const AppealAggregator = globalContext.requireDefault('campaignui/feature/editorial-appeal/aggregator');

const locationI18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/location-targets');
const commonI18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ads-tab/common');
const errI18n = globalContext.require('component/api-errors-campaign/index');
const editorialRejection = globalContext.require('component/api-errors-campaign/map/editorialRejectionFwdLinkId');
const { handleSortChange } = globalContext.require('campaignui/feature/bingadsgrid/service/state-manager-helper');
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');
const Hotkeys = globalContext.require('component/hotkeys/index');
const CopyPaste = globalContext.require('module/view/bulkEdit/copyPaste');
const state = globalContext.require('component/state/index');
const preferenceService = globalContext.requireDefault('component/preference/service/index');
const popupContentKey = _.uniqueId(`${keyVault.hoveringPopup}-`);

const permissions = appConfig.get('Permissions');

export class AdsGrid extends DeterministicWrapperView {
  viewFactory({
    router,
    scope,
    filterService,
    preferenceConfig,
    getGridConfig,
    getPlugins,
    isForVNext = false,
    pageName,
    preexecuteFlag = false,
    campaignContext,
    newI18n,
    deps,
    serverContext,
    shouldSendOpportunityLog,
    logOpportunity,
    adsTabPerfMarker,
  }) {
    this.isInitialGrid = true;
    this.gridRemoved = false;
    this.instrumentation = instrumentation;

    const gridConfig = getGridConfig(this);

    this.deps = deps;
    this.serverContext = serverContext;
    this.preferenceService = preferenceService;
    this.shouldSendOpportunityLog = shouldSendOpportunityLog;
    this.logOpportunity = logOpportunity;
    this.adsTabPerfMarker = adsTabPerfMarker;
    this.gridName = gridConfig.name;
    this.isForVNext = isForVNext;
    this.scope = scope;
    this.router = router;
    this.newI18n = newI18n;
    this.filterService = filterService;
    instrumentation.addScenario(instrumentationSchema);
    this.logScenario = instrumentation.adsGrid.create();
    this.activityLogger = this.logScenario.loadRSAAssetReportLink && this.logScenario.loadRSAAssetReportLink.create();
    this.activityAdStrengthLogger = this.logScenario.adStrength && this.logScenario.adStrength.create();


    this.rejectionPopupContainerSelector = '#rejectionContainer';
    this.ExtaDeprecatedPopupContainerSelector = '#deprecatedContainer';
    this.popoverId = 'delivery-status-popup-callout';
    this.appealAggregator = null;
    this.disclaimerAppealAggregator = null;
    this.pageName = pageName;
    this.preexecuteFlag = preexecuteFlag;
    this.campaignContext = campaignContext;
    this.isMCCLabelEnabled = permissions ? permissions.IsAccountLabelsEnabled : false;
    this.isEditorialCategoryEnabled = permissions && permissions.IsEditorialCategoryEnabled;
    this.isEnabledForGoogleImportVideoAd = permissions && permissions.IsGoogleImportVideoAdsEnabled;

    if (!permissions.dynamic.AdsPageDFMV2) {
      // If DFMv2 is disabled, we need to add the error handler for dataSource
      setUpErrorHandling({
        i18n,
        scope,
        gridView: this,
        dataSource: gridConfig.dataSource,
      });
    }

    const RequestExceptionCallBack =
      (adsGrid, isAdGroupLevel, container = null, render = false) => {
        if (!this || !this.appealAggregator) {
          this.appealAggregator = new AppealAggregator({
            grid: adsGrid.grid,
            controller: 'Ads',
            submit: 'SubmitAppeal',
            menuToFollow: 'button-ad-create',
            isForVNext: this.isForVNext,
            permissions,
            instrumentation,
          });
        }
        this.appealAggregator.openBulkEditor(
          i18n.getString('bulkAdAppealTitle'),
          container, isAdGroupLevel ? 'Ad' : null,
          render,
          i18n.getString('RequestException_Ads_Info_Message')
        );
        return this.appealAggregator.bulkEditor;
      };

    const RequestDisclaimerExceptionCallBack =
      (adsGrid) => {
        if (!this || !this.disclaimerAppealAggregator) {
          this.disclaimerAppealAggregator = new AppealAggregator({
            grid: adsGrid.grid,
            controller: 'Extension',
            submit: 'SubmitAppeal',
            menuToFollow: 'button-ad-create',
            isForVNext: this.isForVNext,
            permissions,
            instrumentation,
            requestExceptionFromPopup: true,
          });
        }
        this.disclaimerAppealAggregator.openBulkEditor(
          i18n.getString(_TL_('This extension does not meet Microsoft Advertising policies. If you feel your content falls within policy, please submit an appeal and we will happily review the content again. ')),
          null, null,
          false, null,
          _.extend(this.scope.values, { type: 'disclaimer' })
        );
        return this.disclaimerAppealAggregator.bulkEditor;
      };


    const RequestDisclaimerExceptionCallBackFromPopup = (adsGrid, id) => {
      const activity = this.instrumentation && this.instrumentation.editorial && this.instrumentation.editorial.create().clickRequestException.create();

      if (activity && activity.trace) {
        activity.trace(`click discliamer request exception for ad: ${id}`);
      }

      if (this.isForVNext) {
        this.trigger('closeDeliveryStatusPopup');
      } else {
        $(this.rejectionPopupContainerSelector).hide();
      }

      adsGrid.grid.gridView.deselectAll();
      adsGrid.grid.gridView.selectRow(id);
      const appealView = RequestDisclaimerExceptionCallBack(adsGrid);
      adsGrid.overrideWith(appealView.render());

      appealView.on('hide', () => {
        adsGrid.overrideWith(null);
        appealView.remove();
      });

      return appealView;
    };

    const RequestExceptionCallBackFromPopup = (adsGrid, isAdGroupLevel, id) => {
      const activity = this.instrumentation && this.instrumentation.editorial && this.instrumentation.editorial.create().clickRequestException.create();

      if (activity && activity.trace) {
        activity.trace(`click request exception for ad: ${id}, isAdGroupLevel: ${isAdGroupLevel}`);
      }

      if (this.isForVNext) {
        this.trigger('closeDeliveryStatusPopup');
      } else {
        $(this.rejectionPopupContainerSelector).hide();
      }

      adsGrid.grid.gridView.selectRow(id);

      if (isForVNext) {
        const appealView = RequestExceptionCallBack(adsGrid, isAdGroupLevel);
        adsGrid.overrideWith(appealView.render());

        appealView.on('hide', () => {
          adsGrid.overrideWith(null);
          appealView.remove();
        });

        return appealView;
      }

      return RequestExceptionCallBack(adsGrid, isAdGroupLevel, '.ads-bulk-appeal', true);
    };

    this.RequestExceptionCallBack = RequestExceptionCallBack;
    this.MMABulkConversion = StyledMMABulkConversionView;
    this.RequestExceptionCallBackFromPopup = RequestExceptionCallBackFromPopup;
    this.RequestDisclaimerExceptionCallBackFromPopup = RequestDisclaimerExceptionCallBackFromPopup;

    this.grid = pgridFactory({
      gridConfig,
      plugins: getPlugins(this),
      preferenceConfig,
    });

    this.grid.gridView.on('willUpdate', (changes) => {
      // for the perf issue, should not patch preference when init view
      if (this.isInitialGrid) {
        this.isInitialGrid = false;
        return;
      }
      const data = this.grid.gridView.dataSource.rawData;
      if (data && data.value) {
        const rsaList = _.filter(data.value, item => item.Type && item.Type === 'ResponsiveSearch');
        if (_.size(rsaList) > 0 && this.activityLogger) {
          this.activityLogger.trace('Load RSA in Ads Grid');
        }
      }
      handleSortChange(changes, this.grid.gridView, gridConfig.name);
    });

    this.grid.gridView.once('didReload', () => {
      this.grid.gridView.once('didRedraw', () => {
        PageReady.pageIsReady(this.pageName);
      }, this);
    });

    const bulkEditGridOptions = {
      filterService,
      gridView: this.grid.gridView,
      pgridPlugins: this.grid,
      isForVNext: this.isForVNext,
    };

    if (!this.preexecuteFlag && permissions.IsBulkEditCopyPasteEnabled) {
      Hotkeys.on('copy', CopyPaste.copy.bind(CopyPaste, 'ad', bulkEditGridOptions));

      if (this.isForVNext) {
        Hotkeys.on('paste', this.grid.paste.onClick);
      } else {
        Hotkeys.on('paste', CopyPaste.paste.bind(CopyPaste, bulkEditGridOptions));
      }
      Hotkeys.setup();
    }

    if (isForVNext) {
      this.filterService.on('filter-state-changed', () => {
        if (!this.gridRemoved) {
          const oldQuery = _.result(this.grid.gridView.get('query'), 'filter');
          const newQuery = _.result(this.filterService.getCombinedFilter(), 'query');
          // If filter has not changed, do not update grid query
          if (!_.isEqual(oldQuery, newQuery)) {
            updateGridQuery(
              this.grid.gridView,
              this.filterService.getCombinedFilter(), true
            );
          }
        }
      });
    }

    this.grid.gridView.on('didUpdate', () => {
      this.gridDidUpdateHandler();
    });

    this.grid.pagination.on('change:page-number', (pageNumber) => {
      this.setPageNumberToState(pageNumber);
    });

    return this.grid.gridView;
  }

  gridDidUpdateHandler = () => {
    const data = this.grid.gridView.dataSource.rawData;
    setTimeout(() => { // wait for toolbar displayed
      if (data) {
        const count = data['@ns.unfiltered.count'];
        this.setCreateBtnDisabled(this.shouldDisableCreateBtn(count));
      } else {
        this.setCreateBtnDisabled(this.shouldDisableCreateBtn(null));
      }
    });
  };

  remove() {
    if (!this.preexecuteFlag && permissions.IsBulkEditCopyPasteEnabled) {
      Hotkeys.reset();
    }
    this.gridRemoved = true;
    return super.remove();
  }

  setPageNumberToState(number) {
    state.set('pgridPageNumber', _.defaults({
      [this.gridName]: number,
    }, state.get('pgridPageNumber') || {}));
  }

  setCreateBtnDisabled(shouldDisableCreateBtn) {
    if (shouldDisableCreateBtn) {
      $('#button-ad-create').attr('disabled', 'disabled');
    } else {
      $('#button-ad-create').prop('disabled', false);
    }
  }

  shouldDisableCreateBtn(count) {
    const { waterfallFilters } = this.filterService;
    const getCampaignTypeGlobalFilter = () => {
      const rawFilter = chain(waterfallFilters).first().value();
      return getFilteredCampaignTypeFromRawFilter(rawFilter);
    };

    let campaign = appConfig.get('CurrentCampaign');
    const CurrentAccount = appConfig.get('CurrentAccount');
    if (this.isForVNext && this.campaignContext) {
      campaign = this.campaignContext.currentCampaign;
    }
    const isAudienceFeedBasedCampaignLevel = campaign &&
      (campaign.CampaignType === 8 || campaign.CampaignType === 'Audience') &&
      _.result(_.first(campaign.CampaignSettings), 'ProviderId');
    const { IsReadOnlyUser, IsResponsiveAdForSSCEnabled } = permissions;

    if (campaign && (campaign.CampaignSubType === 'ShoppingSmartAds' || campaign.CampaignSubType === 12)) {
      return count >= (IsResponsiveAdForSSCEnabled ? 2 : 1);
    }

    // if current campaign is hotel campaign, disable create button.
    if (permissions.dynamic && permissions.dynamic.HotelCampaign) {
      if (getCampaignTypeGlobalFilter() === 32 || (campaign && (campaign.CampaignType === 32 || campaign.CampaignType === 'Hotel'))) {
        return true;
      }
    }

    return !!((CurrentAccount && CurrentAccount.Status === 32) ||
      isAudienceFeedBasedCampaignLevel || IsReadOnlyUser);
  }

  renderTrunk() {
    if (this.isForVNext) {
      this.filterService.refreshSource();
      this.filterService.initializeCombinedFilterPromise.then(() => {
        if (!this.gridRemoved) {
          updateGridQuery(
            this.grid.gridView,
            this.filterService.getCombinedFilter(), true
          );
        }
      });
    }
    super.renderTrunk();
  }

  getTargetEnity(event) {
    const key = this.grid.gridView.keyOfElement(event.target);

    return this.grid.gridView.itemWithKey(key);
  }

  onManageLabelsClicked() {
    navigateToManagePage(this.router);
  }

  onLabelClicked(event) {
    const targetEntity = this.getTargetEnity(event);
    filterOnLabelClick(
      event,
      targetEntity.Labels,
      this.grid.gridView,
      this.filterService,
      this.isMCCLabelEnabled
    );
  }

  onLabelsRowClicked(event) {
    const targetEntity = this.getTargetEnity(event);
    let entityName;

    if (targetEntity.Type === 'ExpandedText') {
      if (permissions.IsExpandedTextAdV2Enabled && targetEntity.TitlePart3) {
        entityName = `${targetEntity.TitlePart1} | ${targetEntity.TitlePart2} | ${targetEntity.TitlePart3}`;
      } else {
        entityName = `${targetEntity.TitlePart1} | ${targetEntity.TitlePart2}`;
      }
    } else {
      entityName = targetEntity.Title || targetEntity.Text;
    }

    this.grid.gridView.trigger('willShowLabelsPopup');

    showLabelsPopup({
      labels: targetEntity.Labels,
      entityType: 'Ad',
      entityName,
      container: this.isForVNext ? $('.grid-and-disclaim-container') : this.$el,
      event,
      router: this.router,
      i18n,
      grid: this.grid,
      filterService: this.filterService,
      permissions,
      isMCCLabelEnabled: this.isMCCLabelEnabled,
    });
    if (this.isForVNext) {
      event.preventDefault();
    }
  }

  onLinkToAssets(event) {
    const targetEntity = this.getTargetEnity(event);
    let adType = targetEntity.Type;
    const campaignType = targetEntity.Campaign && targetEntity.Campaign.CampaignType;
    const CampaignSubType = targetEntity.Campaign && targetEntity.Campaign.CampaignSubType;
    let adSubType = targetEntity.AdSubType;
    if (campaignType === 'Audience' && CampaignSubType === 'AudienceStatic' && adSubType === null) {
      adSubType = 'native';
    }

    if (targetEntity.Type === 'Responsive') {
      if (permissions.dynamic && permissions.dynamic.AudienceAssetReport && campaignType === 'Audience') {
        adType = 'Responsive';
      } else if (_.isEmpty(targetEntity.Headline) && _.isEmpty(targetEntity.Headlines) &&
        _.isEmpty(targetEntity.LongHeadline) && _.isEmpty(targetEntity.LongHeadlines) &&
        _.isEmpty(targetEntity.Text) && _.isEmpty(targetEntity.Descriptions)) {
        adType = 'DynamicResponsive';
      }
    }
    if (this.activityLogger) {
      this.activityLogger.trace(`Click Link to Assets:${adType}`);
    }
    this.router.navigateToRoute('adgroup_ad_assets', Object.assign({
      customer_id: state.get('customer_id'),
      account_id: state.get('account_id'),
      campaign_id: (targetEntity.Campaign && targetEntity.Campaign.Id) || state.get('campaign_id'),
      adgroup_id: (targetEntity.AdGroup && targetEntity.AdGroup.Id) || state.get('adgroup_id'),
      ad_id: targetEntity.Id,
      ad_type: adType,
      campaign_type: campaignType,
    }, adSubType ? {
      ad_sub_type: adSubType,
    } : {}));
  }

  addEditorialHandler({ ad, popupView }) {
    popupView.$('#requestException').on('click', _.partial(this.RequestExceptionCallBackFromPopup, this, false, ad.Id));
    popupView.$('#requestExceptionforExtension').on('click', _.partial(this.RequestDisclaimerExceptionCallBackFromPopup, this, ad.Id));
    popupView.$('#requestExceptionAdgroupLevel').on('click', _.partial(this.RequestExceptionCallBackFromPopup, this, true, ad.Id));
    const activity = this.instrumentation && this.instrumentation.editorial && this.instrumentation.editorial.create().toggleCountryList.create();
    registerCountryListEvent(popupView.$el, activity, { id: ad.Id, entity: 'ad' });
  }

  // this is only used by new ui
  hideRejectionPopup(e) {
    if ($(e.relatedTarget).closest(`#${this.popoverId}`).length === 0) {
      this.trigger('closeDeliveryStatusPopup');
    }
  }

  showRejectionReason(event) {
    const actionLinks = [];
    if (this.isForVNext) {
      const entity = this.getTargetEnity(event);
      if (entity && entity.DeliveryStatusDetails &&
        entity.DeliveryStatusDetails.includes('ShoppingCampaignNoOffersPresent')) {
        // use all site map to get merchant center url for different scopes
        const url = getRoutePathById(allSiteMap, 'merchant-center');
        const uri = new URI(url);
        appendScopeParameter(uri, this.scope);
        actionLinks.ShoppingCampaignNoOffersPresent = uri.toString();
      }

      if (this.isEnabledForGoogleImportVideoAd &&
        entity && entity.DeliveryStatusDetails &&
        entity.DeliveryStatusDetails.includes('GoogleImportAdVideoNeedRepair')) {
        actionLinks.GoogleImportAdVideoNeedRepair = _.get(entity, 'Videos[0].Asset.SourceUrl') || 'Unavailable';
      }
    }
    const factory = new DeliveryStatusPopupFactory({ grid: this.grid, i18n });
    const ad = factory.getEventTargetEntity(event);

    factory.showRejectionPopup({
      event,
      actionLinks,
      getCustomTemplate: _.partial(getReasonTemplates, {
        event,
        scope: this.scope,
        errI18n,
        locationI18n,
        editorialRejection,
        i18n,
        commonI18n,
        ad,
        grid: this.grid,
        logScenario: this.logScenario,
        permissions,
      }),
      useCustomTemplate: deliveryStatus =>
        isDeliveryStatusDisapproved(deliveryStatus) || (permissions && permissions.dynamic.AdExtensionInlineAppeal && _.contains(['DisclaimerInfoRejected', 'DisclaimerInfoApprovedLimited', 'DisclaimerInfoRejectedAppealRejected', 'DisclaimerInfoApprovedLimitedAppealRejected'], deliveryStatus)),
      containerSelector: this.rejectionPopupContainerSelector,
      view: this,
      popoverId: this.popoverId,
      instrumentation,
      inContextRepairRecommedationType: 'ad',
      afterRender: popup => this.addEditorialHandler({ ad, popupView: popup }),
      isEditorialCategoryEnabled: this.isEditorialCategoryEnabled,
    });
  }

  closeAdStrengthPopup() {
    if (window.popupManager) {
      window.popupManager.releaseLock(popupContentKey);
    }
    ReactDOM.unmountComponentAtNode(document.querySelector(this.rejectionPopupContainerSelector));
    this.adStrengthPopoverShown = false;
  }

  /* istanbul ignore next */
  hideAdStrengthPopup(e) {
    if ($(e.relatedTarget).closest('.ad-adstrength-callout').length === 0) {
      this.closeAdStrengthPopup();
    }
  }

  showAdStrengthPopup(event) {
    const { customerId: cid, accountId: aid } = this.scope;
    const containerDOM = document.querySelector(this.rejectionPopupContainerSelector);
    const adStrengthEntity = this.getTargetEnity(event);
    const {
      AdStrength, AdStrengthActionItems, Id, Campaign, AdGroup, FinalUrls,
    } = adStrengthEntity;

    if (AdStrength !== 'Pending' && (!AdStrengthActionItems || AdStrengthActionItems.length === 0)) {
      return;
    }
    if (window.popupManager) {
      window.popupManager.forceAcquireLock(popupContentKey);
    }

    this.activityAdStrengthLogger.signal(`ad strength - hover the column :{"AdId":"${Id}","OrderId":"${AdGroup.Id}","CampaignId":"${Campaign && Campaign.Id}","cid":"${cid}","aid":"${aid}","FinalUrls": "${FinalUrls && FinalUrls.toString()}","adstrength":"${AdStrength}"}`);
    renderSuggestionPopup({
      i18n, event, containerDOM, adStrengthEntity, adsGrid: this, closeAdStrengthPopup: this.closeAdStrengthPopup.bind(this),
    });

    this.adStrengthPopoverShown = true;
  }

  /* istanbul ignore next */
  showDeprecateReason(event) {
    const { customerId: cid, accountId: aid } = this.scope;
    const targetEntity = this.getTargetEnity(event);
    const campaignId = (targetEntity.Campaign && targetEntity.Campaign.Id) || state.get('campaign_id');
    const adgroupId = (targetEntity.AdGroup && targetEntity.AdGroup.Id) || state.get('adgroup_id');
    const type = targetEntity.Type;
    const containerDOM = document.querySelector(this.rejectionPopupContainerSelector);
    const href = `/campaign/vnext/recommendations/All?adGroupId=${adgroupId}&aid=${aid}&campaignId=${campaignId}&cid=${cid}&recommendationType=ResponsiveSearchAdsOpportunity`;
    const styles = mergeStyleSets({
      callout: {
        width: 320,
        padding: '20px 24px',
        fontSize: '12px',
      },
      title: { marginBottom: 12 },
      body: {},
    });
    ReactDOM.render(
      (
        <Callout className={`ad-type-callout ${styles.callout}`} target={event.target} directionalHint={DirectionalHint.topCenter} role="alert">
          {type === 'Text' &&
          <div className={styles.title} block variant="xLarge">
            {this.newI18n.getString(_TL_('Standard Text ads will stop serving soon. We recommend upgrading your existing Standard Text ads to Responsive Search ads. To upgrade, visit the '))}
            <b>{this.newI18n.getString(_TL_('Recommendations'))}</b>
            {this.newI18n.getString(_TL_(' page: From the navigation menu on the left, hover over '))}
            <b>{this.newI18n.getString(_TL_('Campaigns'))}</b>
            {this.newI18n.getString(_TL_(' and select '))}
            <b>{this.newI18n.getString(_TL_('Recommendations'))}</b>
            {this.newI18n.getString(_TL_('. View and apply our recommendations for automatically creating new Responsive Search ads. '))}
            <HelpLink query="help:app54711">
              {this.newI18n.getString(_TL_('Learn more'))}
            </HelpLink>
          </div>
          }
          {type === 'ExpandedText' &&
          <div className={styles.title} block variant="xLarge">
            {this.newI18n.getString(_TL_('You can no longer create new or edit existing expanded text ads. We recommend upgrading your existing expanded text ads to responsive search ads to optimize performance and drive ROI (return on investment).'))}
            <br />
            {this.newI18n.getString(_TL_('Please review your '))}<a href={href} > {this.newI18n.getString(_TL_('responsive search ad recommendations'))}</a>{this.newI18n.getString(_TL_(' to make updates. '))}
          </div>
          }
        </Callout>
      ),
      containerDOM
    );

    this.deprecatePopoverShown = true;
  }
  /* istanbul ignore next */
  hideDeprecatePopover(e) {
    if ($(e.relatedTarget).closest('.ad-type-callout').length === 0) {
      ReactDOM.unmountComponentAtNode(document.querySelector(this.rejectionPopupContainerSelector));
      this.deprecatePopoverShown = false;
    }
  }

  get className() { return 'ads-grid'; }
}

